import React, { useRef, useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, IconButton } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";

import {
  setTaxCategories,
  taxCategoryCellRenderer,
} from "../../../../component/cellRenderers/renderers";
import SideNav from "../../../../component/layouts/sideNav";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "../../../../component/modal/alertModal";

import {
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "../../../../resources/constant/colors/theme";
import {
  ADD,
  DELETE,
  MASTER,
  TAXES,
} from "../../../../resources/constant/strings/string";
import { PATH_TAX } from "../../../../resources/constant/url/url";

import { APIMASTER } from "../../../../apihandler/master";

import Modal from "./modal";

export default function Tax() {

  const gridRef = useRef();

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  let initialValue = {};

  const setInitialValueForAdd = () => {
    initialValue = {
      id: 0,
      serialNo: 0,
      taxName: "",
      percentage: 0.0,
      calculationLevel: 0,
      description: "",
      categoryId: 0,
      expenseAccountId: null,
      revenueAccountId: null,
      liabilityAccountId: null,
    };
  };

  const deleteModel = () => {
    if (selectedRowIds.length === 0) {
      return;
    }
    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      console.log("delete the selected ids: ", selectedRowIds);
      APIMASTER.DeleteTaxes(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
            setOpenModal(false);
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
            setOpenModal(false);
          }
        });
    });
  };

  const updateModel = (model) => {
    gridRef.current.api.applyTransaction({ update: [model] });
  };

  const addModel = (model) => {
    gridRef.current.api.applyTransaction({ add: [model], addIndex: 0 });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const [formTitle, setFormTitle] = useState("Add Tax");
  const handleEditData = (oldData) => {
    console.log(oldData);
    initialValue = {
      id: oldData.id,
      serialNo: oldData.serialNo,
      taxName: oldData.taxName,
      percentage: oldData.percentage,
      categoryId: oldData.categoryId || 0,
      calculationLevel: oldData.calculationLevel,
      description: oldData.description,
    };
    setFormData(initialValue);
    setViewOnlyModel(false);
    setFormTitle("Edit Tax");
    setOpenModal(true);
  };

  const handleViewData = (oldData) => {
    console.log(oldData);
    initialValue = {
      id: oldData.id,
      serialNo: oldData.serialNo,
      taxName: oldData.taxName,
      percentage: oldData.percentage,
      categoryId: oldData.categoryId || 0,
      calculationLevel: oldData.calculationLevel,
      description: oldData.description,
    };
    setFormData(initialValue);
    setFormTitle("View Tax");
    setViewOnlyModel(true);
    setOpenModal(true);
  };

  const columnDefs = [
    {
      flex: 0.6,
      filter: false,
      floatingFilter: false,
      headerName: "SL NO",
      field: "serialNo",
      checkboxSelection: false,
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1.5,
      headerName: "Tax Name",
      field: "taxName",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      flex: 0.8,
      headerName: "Category",
      field: "categoryId",
      valueGetter: (params) => {
        return taxCategoryCellRenderer({ value: params.data.categoryId });
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 0.8,
      headerName: "Percentage",
      field: "percentage",
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
      filter: false,
      floatingFilter: false,
    },
    {
      flex: 1.5,
      wrapText: true,
      autoHeight: true,
      headerName: "Description",
      field: "description",
      filterParams: {
        caseSensitive: false,
        defaultOption: "contains",
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },

    {
      flex: 0.8,
      // this is for actions, edit and delete
      headerName: "Actions",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            aria-label="edit"
            onClick={() => {
              handleEditData(params.data);
            }}
          >
            <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
          <IconButton
            aria-label="visibility"
            onClick={() => {
              handleViewData(params.data);
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
  };

  const onGridReady = (params) => {
    APIMASTER.TaxCategories()
      .then((resp) => resp.json())
      .then((resp) => {
        setTaxCategories(resp.response);
      })
      .then((resp) => {
        APIMASTER.Taxes()
          .then((resp) => resp.json())
          .then((resp) => {
            if (resp.statusCode !== 200) {
              setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
              return;
            }
            const data = resp.response.sort((a, b) => b.id - a.id);
            params.api.applyTransaction({ add: data });
          });
      });
  };

  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [viewOnlyModel, setViewOnlyModel] = useState(false);

  return (
    <SideNav
      currentComponent={MASTER}
      subComponent={TAXES}
      subComponentPath={PATH_TAX}
      detailComponent=""
    >
      {openModal && (
        <Modal
          style={{ align: "center" }}
          setOpenModal={setOpenModal}
          updateModel={updateModel}
          addModel={addModel}
          viewModel={viewOnlyModel}
          model={formData}
          formTitle={formTitle}
        ></Modal>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            setInitialValueForAdd();
            setFormData(initialValue);
            setFormTitle("Add Tax");
            setViewOnlyModel(false);
            setOpenModal(true);
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={() => {
            deleteModel();
          }}
          variant="outlined"
          sx={{
            borderColor: SPANISH_GREEN,
          }}
          style={{
            //backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>
      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
          ></AgGridReact>
        </div>
      </div>
    </SideNav>
  );
}
